import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { profileData } from '@/mocks/articles';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于友田真希官方粉丝应援会',
  description: '友田真希官方粉丝应援会，致力于为友田真希粉丝提供最新动态、作品资讯与应援资源。',
  mainEntity: {
    '@type': 'Organization',
    name: '友田真希官方粉丝应援会',
    url: 'https://huanyung.cn/',
    description: '友田真希官方粉丝应援组织',
    foundingDate: '2015',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@huanyung.cn',
    },
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '如何加入友田真希官方粉丝应援会？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过本页底部的Newsletter订阅表单留下邮箱地址，我们会定期发送入会邀请和最新活动信息。此外，您也可以关注友田真希的官方社交媒体账号获取最新动态。',
      },
    },
    {
      '@type': 'Question',
      name: '友田真希的下一场粉丝见面会什么时候举办？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '友田真希2026年「真希の部屋」粉丝见面会正在巡回举办中。名古屋站和大阪站的日程请关注本站最新动态或分类页面中的「艺能活动」版块。',
      },
    },
    {
      '@type': 'Question',
      name: '友田真希的最新写真集在哪里可以购买？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '友田真希2026夏季写真集《夏の記憶》将于8月25日起在日本全国各大书店及线上平台同步发售，定价3,850日元（含税）。首批预订还可获得限量版签名明信片。',
      },
    },
    {
      '@type': 'Question',
      name: '如何获取友田真希的最新动态和新闻？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以关注本网站首页的「最新动态」栏目，我们将第一时间更新友田真希的相关新闻、作品信息和活动资讯。同时也可以订阅我们的Newsletter获取邮件通知。',
      },
    },
  ],
};

export default function AboutPage() {
  const [contactFormStatus, setContactFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [newsletterStatus, setNewsletterStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setContactFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      return;
    }
    const formData = new FormData(form);
    formData.delete('website_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u1lslca23r0tr1aorg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* raw text */ }
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed as Record<string, string>)?.message || text;
      if (res.ok && (parsed as Record<string, string>)?.code === 'OK') {
        setContactFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setContactFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      } else {
        setContactFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      }
    } catch {
      setContactFormStatus({ type: 'error', message: '网络错误，请检查网络后重试。' });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setNewsletterStatus({ type: 'success', message: '订阅成功！请查收确认邮件。' });
      return;
    }
    const formData = new FormData(form);
    formData.delete('website_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9u1lslca23r0tr1aos0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* raw text */ }
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed as Record<string, string>)?.message || text;
      if (res.ok && (parsed as Record<string, string>)?.code === 'OK') {
        setNewsletterStatus({ type: 'success', message: '订阅成功！请查收确认邮件。' });
        form.reset();
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        setNewsletterStatus({ type: 'error', message: serverMsg || '订阅失败，请稍后再试。' });
      } else {
        setNewsletterStatus({ type: 'error', message: serverMsg || '订阅失败，请稍后再试。' });
      }
    } catch {
      setNewsletterStatus({ type: 'error', message: '网络错误，请检查网络后重试。' });
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解友田真希官方粉丝应援会 - 品牌介绍、艺人履历、口碑评价与联系方式。友田真希，日本知名艺人，汇聚最新动态与作品资讯。"
        keywords="友田真希 个人资料,友田真希 粉丝,友田真希 品牌介绍,友田真希 联系方式"
        canonicalUrl="https://huanyung.cn/about"
        schema={{ ...aboutSchema }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="w-full lg:w-1/2">
                <p className="text-primary-500 text-xs tracking-[0.2em] uppercase mb-2 font-label">About Us</p>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  关于友田真希官方粉丝应援会
                </h1>
                <p className="text-foreground-600 leading-relaxed mb-4 text-sm">
                  友田真希官方粉丝应援会成立于2015年，是一个由热爱友田真希的粉丝自发组织、官方认可的非营利性应援团体。我们致力于为全球华人粉丝提供最全面、最及时的友田真希相关资讯，搭建粉丝之间交流与分享的平台。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4 text-sm">
                  多年来，应援会在每一次友田真希的重要活动中都积极参与应援，包括影视作品发布会、粉丝见面会、写真集发售、生日庆典等。我们坚信，粉丝的力量可以让偶像走得更远。
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm">
                  我们的使命：汇聚每一份热爱，传递每一份支持。用心记录友田真希的每一个闪光时刻，与全球粉丝一同见证她的艺能之路。
                </p>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="w-full aspect-video rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20female%20celebrity%20warm%20portrait%20with%20fans%20at%20event%20venue%2C%20waving%20and%20smiling%2C%20joyful%20atmosphere%2C%20soft%20golden%20lighting%2C%20community%20and%20connection%20theme%2C%20uplifting%20editorial%20photography&width=1200&height=675&seq=yutamaki-about-01&orientation=landscape"
                    alt="友田真希 粉丝应援会"
                    title="友田真希 粉丝应援会"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Artist Bio */}
          <section className="mb-16 py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <p className="text-primary-500 text-xs tracking-[0.2em] uppercase mb-2 font-label text-center">Artist Profile</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
                友田真希 个人履历
              </h2>

              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                  {[
                    { label: '出生日期', value: profileData.birthdate },
                    { label: '出生地', value: profileData.birthplace },
                    { label: '身高', value: profileData.height },
                    { label: '血型', value: profileData.bloodType },
                    { label: '出道年份', value: profileData.debut },
                    { label: '所属事务所', value: profileData.agency },
                    { label: '爱好', value: profileData.hobbies.join(' / ') },
                    { label: '特长', value: profileData.specialties.join(' / ') },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground-950">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-200/70 pt-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">艺人简介</h3>
                  {profileData.biography.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-3 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Awards & Recognition */}
          <section className="mb-16">
            <div className="max-w-4xl mx-auto">
              <p className="text-primary-500 text-xs tracking-[0.2em] uppercase mb-2 font-label text-center">Awards & Recognition</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
                获奖与荣誉
              </h2>

              <div className="space-y-4">
                {profileData.awards.map((award, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-background-50 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                      <span className="font-heading text-lg font-bold">{award.year}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                        {award.title}
                      </h3>
                      <p className="text-sm text-foreground-600">{award.work}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact & Newsletter */}
          <section className="mb-16 py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Contact Form */}
                <div>
                  <p className="text-primary-500 text-xs tracking-[0.2em] uppercase mb-2 font-label">Contact</p>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">联系我们</h2>
                  <p className="text-sm text-foreground-600 mb-6">
                    如有任何疑问或合作需求，欢迎通过以下表单联系我们。
                  </p>

                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">最多500字</p>
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap"
                    >
                      发送留言
                    </button>

                    {contactFormStatus && (
                      <div
                        className={`text-sm p-3 rounded-lg ${
                          contactFormStatus.type === 'success'
                            ? 'bg-accent-50 text-accent-700 border border-accent-200'
                            : 'bg-primary-50 text-primary-700 border border-primary-200'
                        }`}
                      >
                        {contactFormStatus.message}
                      </div>
                    )}
                  </form>
                </div>

                {/* Newsletter */}
                <div>
                  <p className="text-primary-500 text-xs tracking-[0.2em] uppercase mb-2 font-label">Newsletter</p>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">订阅最新资讯</h2>
                  <p className="text-sm text-foreground-600 mb-6">
                    订阅Newsletter，第一时间获取友田真希的最新动态、活动资讯与独家内容。
                  </p>

                  <form
                    onSubmit={handleNewsletterSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="newsletter-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="newsletter-name"
                        name="name"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="newsletter-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="newsletter-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <p className="text-xs text-foreground-500">
                      订阅即表示您同意接收友田真希官方粉丝应援会的邮件通知。我们承诺不会泄露您的个人信息。
                    </p>
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap"
                    >
                      立即订阅
                    </button>

                    {newsletterStatus && (
                      <div
                        className={`text-sm p-3 rounded-lg ${
                          newsletterStatus.type === 'success'
                            ? 'bg-accent-50 text-accent-700 border border-accent-200'
                            : 'bg-primary-50 text-primary-700 border border-primary-200'
                        }`}
                      >
                        {newsletterStatus.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}